'use client';

// ===================================================
// GAM Command Center — Tiptap Editor V3
// Phase 2: Image, File, Table, Embed, Callout
// + Phase 1.5: Toggle, Block Handles, Colors, Code
// ===================================================

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { TextStyle, FontFamily, FontSize, LineHeight } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/react';
import { useDebouncedCallback } from 'use-debounce';
import { SuggestionPluginKey } from '@tiptap/suggestion';
import { NodeRange } from '@tiptap/extension-node-range';

// Phase 1B (2026-04-30): framework-pure pieces moved to @gam/editor.
// Relative imports since this file IS part of the package.
import {
  SlashCommands,
  GAM_SLASH_MENU_EVENT,
  GAM_SLASH_DISMISS_EVENT,
  dispatchSlashDismiss,
  type GamSlashMenuEventDetail,
} from './extensions/SlashCommands';
import { SlashFullMenu } from './SlashFullMenu';
import type { Range as SlashRange } from '@tiptap/core';
import type { SlashCategory } from './slash-command-filter';
import { ToggleDetails, DetailsSummary, DetailsContent } from './extensions/Toggle';
import { CodeBlockGam } from './extensions/CodeBlockGam';
import { ImageBlock } from './extensions/ImageBlock';
import { FreePosition, setFreePositionMode } from './extensions/FreePosition';
import { MediaDrop } from './extensions/MediaDrop';
import { FileBlock } from './extensions/FileBlock';
import { EmbedBlock } from './extensions/EmbedBlock';
import { CalloutBlock } from './extensions/CalloutBlock';
import { tableExtensions } from './extensions/TableSetup';
import { FieldBlock } from './extensions/FieldBlock';
import { ObjectRef } from './extensions/ObjectRef';
import { TextToRecordBridge } from './extensions/TextToRecord';
import { BlockHandle } from './BlockHandle';
import { FloatingToolbar } from './FloatingToolbar';
import { ObjectRefPopup } from './ObjectRefPopup';
import type { TiptapEditorProps } from './types';
import { buildLeanExtensions, CHROME_BY_TIER } from './editor-tiers';
import { DEFAULT_EDITOR_TRANSLATIONS } from './editor-translations';
import './editor.css';

// ─── Default empty content ───────────────────────────
const DEFAULT_CONTENT: JSONContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

// ─── Slash menu state ────────────────────────────────
// `categories` and `query` are carried through rather than flattened away:
// the menu is categorised, and it is the extension that already filtered the
// catalogue against what was typed, so re-deriving either here would just be
// a second source of truth for the same list.
interface SlashState {
  /** Caret box in viewport coordinates, exactly as the extension reported it. */
  caret: { top: number; bottom: number; left: number } | null;
  range: SlashRange | null;
  categories: SlashCategory[];
  query: string;
}

/** Module-level singleton: the "closed" state must keep a STABLE identity,
 *  otherwise every close event produces a new object and re-renders. */
const SLASH_CLOSED: SlashState = { caret: null, range: null, categories: [], query: '' };

// ─── Per-block Placeholder Config (language-aware) ───
function getPlaceholderMap(et: Record<string, string>): Record<string, string | ((node: { attrs?: { level?: number } }) => string)> {
  return {
    paragraph: et.placeholderSlashMenu,
    heading: (node: { attrs?: { level?: number } }) => {
      const level = node.attrs?.level || 1;
      const labels: Record<number, string> = {
        1: et.placeholderHeading1,
        2: et.placeholderHeading2,
        3: et.placeholderHeading3,
      };
      return labels[level] || et.placeholderHeading;
    },
    bulletList: et.placeholderListItem,
    orderedList: et.placeholderListItem,
    taskList: et.placeholderTask,
    blockquote: et.placeholderBlockquote,
    codeBlock: et.placeholderCode,
    detailsSummary: et.placeholderToggle,
    callout: et.placeholderCallout,
  };
}

// ─── Component ───────────────────────────────────────
export function TiptapEditor({
  uploadAttachment,
  mediaNodeView,
  content,
  onChange,
  onSave,
  placeholder,
  editable = true,
  tier = 'full',
  autoFocus = false,
  className = '',
  recordId,
  saveStatus = 'idle',
  lastSavedAt,
  onConflictReload,
  direction = 'rtl',
  translations,
  gibberishConverter,
  aiTextAction,
  searchEntities,
  onNavigateRef,
  onCreateFromText,
  onEditorReady,
}: TiptapEditorProps) {
  const et = useMemo(() => ({ ...DEFAULT_EDITOR_TRANSLATIONS, ...translations }), [translations]);
  const placeholderMap = useMemo(() => getPlaceholderMap(et), [et]);
  const defaultPlaceholder = et.placeholderSlashMenu;

  // The extensions array below is built ONCE. Anything render-dependent it
  // needs is read through this ref instead of being closed over, so the
  // extension instances keep a stable identity across re-renders.
  const placeholderRef = useRef({ placeholderMap, defaultPlaceholder, placeholder });
  placeholderRef.current = { placeholderMap, defaultPlaceholder, placeholder };
  // Track internal changes to prevent content sync from resetting the editor
  const isInternalChange = useRef(false);
  const [objectRefOpen, setObjectRefOpen] = useState(false);
  const [freeMode, setFreeMode] = useState(false);
  const mediaRef = useRef({ uploadAttachment, mediaNodeView });
  mediaRef.current = { uploadAttachment, mediaNodeView };

  // ─── Auto-save: 1s debounce after every change ───
  const debouncedSave = useDebouncedCallback((json: JSONContent) => {
    if (onSave) onSave(json);
  }, 1000);

  // Shared by every tier: the resolver reads from a ref, so one configured
  // instance serves the full list and the lean builder alike.
  const placeholderExtension = useMemo(
    () =>
      Placeholder.configure({
        placeholder: ({ node }) => {
          const { placeholderMap: map, defaultPlaceholder: fallback, placeholder: override } =
            placeholderRef.current;
          const type = node.type.name;
          const entry = map[type];
          if (typeof entry === 'function') return entry(node);
          if (typeof entry === 'string') return entry;
          return override || fallback;
        },
        emptyEditorClass: 'gam-editor--empty',
        emptyNodeClass: 'gam-editor__node--empty',
        includeChildren: true,
      }),
    []
  );

  // Built once, on purpose — do NOT inline this back into useEditor.
  //
  // useEditor() is called without a deps array, which puts it on the
  // "compare options every render" path: on each render it diffs the options
  // object against the live editor's and, on any difference, calls
  // editor.setOptions() — which runs view.setProps() + view.updateState(),
  // re-syncing the ProseMirror view underneath whatever the user is doing.
  // extensions are compared element-by-element by identity, so an inline
  // array (with fresh StarterKit.configure() instances every render) never
  // matched and setOptions fired on EVERY render. Autosave supplies those
  // renders on its own timer — saveState 'saving' → 'saved' → 'idle' after
  // 2000ms — which is why an open slash menu died on its own a couple of
  // seconds after it opened, with no user action.
  const fullExtensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: false, // We use CodeBlockGam
      }),
      placeholderExtension,
      TaskList.configure({
        HTMLAttributes: { class: 'gam-tasklist' },
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: { class: 'gam-taskitem' },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'gam-link',
          rel: 'noopener noreferrer',
        },
      }),
      TextStyle,
      // Ships inside @tiptap/extension-text-style — no new dependency. Feeds
      // the language-grouped font picker in the floating toolbar.
      FontFamily,
      // Same package as FontFamily — the selection toolbar's size and
      // line-spacing menus write these two textStyle attributes.
      FontSize,
      LineHeight,
      Color,
      Highlight.configure({
        multicolor: true,
        HTMLAttributes: { class: 'gam-highlight' },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: 'right',
      }),
      // Toggle
      ToggleDetails,
      DetailsSummary,
      DetailsContent,
      // Code
      CodeBlockGam,
      // Phase 2 blocks
      ImageBlock.extend({ addNodeView() { return mediaRef.current.mediaNodeView || null; } }),
      FileBlock.extend({ addNodeView() { return mediaRef.current.mediaNodeView || null; } }),
      FreePosition,
      MediaDrop.configure({ upload: () => mediaRef.current.uploadAttachment }),
      EmbedBlock,
      CalloutBlock,
      // Table
      ...tableExtensions,
      // Field blocks
      FieldBlock,
      // Object reference inline node
      ObjectRef,
      // Text-to-record storage bridge (task creation from the slash menu)
      TextToRecordBridge,
      // Slash commands
      SlashCommands,
      // Multi-block selection. Installed since 2026-07 but never registered,
      // so none of it was reachable: Shift-↑/↓ to extend a selection block by
      // block, Mod-a for the whole doc, modifier+drag to sweep a range, and
      // the .ProseMirror-selectednoderange decoration.
      //
      // key: null makes the mouse gesture always-on (no modifier needed) —
      // Gal wants "mark the area and drag it", not "hold a key and mark".
      // ProseMirror selections are a single contiguous range by design, so
      // this covers lasso/marquee and Shift ranges; cherry-picking
      // non-adjacent blocks is not expressible and is out of scope.
      NodeRange.configure({ key: null }),
    ],
    [placeholderExtension]
  );

  // The tier decides which list mounts. Same stability rule as above: the
  // branch is memoised on the tier so the array keeps its identity, and a tier
  // that changes mid-life deliberately rebuilds the view (documented on the
  // prop) rather than quietly mixing two schemas.
  const extensions = useMemo(
    () => (tier === 'full' ? fullExtensions : buildLeanExtensions(tier, placeholderExtension)),
    [tier, fullExtensions, placeholderExtension]
  );

  // Overlays are derived from the tier, never passed in: each one calls
  // commands that only exist in the full list.
  const chrome = CHROME_BY_TIER[tier];

  // Same reason as `extensions` above: a fresh object here counts as an
  // options difference and re-triggers setOptions() on every render.
  const editorProps = useMemo(
    () => ({
      attributes: {
        class: `gam-editor ${className}`,
        dir: 'auto',
        spellcheck: 'true',
        'data-record-id': recordId || '',
      },
    }),
    [className, recordId]
  );

  const editor = useEditor({
    immediatelyRender: false,
    extensions,
    content: content || DEFAULT_CONTENT,
    editable,
    autofocus: autoFocus ? 'end' : false,
    editorProps,
    onUpdate: ({ editor: e }) => {
      const json = e.getJSON();
      isInternalChange.current = true;
      onChange?.(json);
      debouncedSave(json);
    },
  });

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editor, editable]);

  // Hands the live instance to the host. This is what makes a lean tier
  // usable: with no FloatingToolbar the host draws its own controls, and they
  // need something to call. Fires with null on unmount so a host that keeps it
  // in state does not hold a destroyed editor.
  useEffect(() => {
    if (!onEditorReady) return;
    onEditorReady(editor ?? null);
    return () => onEditorReady(null);
  }, [editor, onEditorReady]);

  // Listen for field save events to insert field blocks
  useEffect(() => {
    if (!editor) return;
    const handleFieldSaved = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.fieldType) {
        editor.chain().focus().setFieldBlock({
          fieldType: detail.fieldType,
          fieldId: detail.fieldId || '',
          label: detail.label || detail.fieldType,
          config: detail.config || {},
        }).run();
      }
    };
    window.addEventListener('cc-field-saved', handleFieldSaved);
    return () => window.removeEventListener('cc-field-saved', handleFieldSaved);
  }, [editor]);

  // Wire ObjectRef storage callbacks (onNavigate + onRequestInsert)
  useEffect(() => {
    if (!editor) return;
    // Absent below the full tier — the ObjectRef extension is not registered
    // there, so its storage slot does not exist and writing to it would throw.
    if (!editor.storage.objectRef) return;
    editor.storage.objectRef.onNavigate = onNavigateRef ?? null;
    editor.storage.objectRef.onRequestInsert = () => setObjectRefOpen(true);
  }, [editor, onNavigateRef]);

  // Wire the text-to-task slash-command bridge (see extensions/TextToRecord.ts).
  // The bubble-menu "משימה" button does not go through this — it calls
  // onCreateFromText directly as a prop (see the FloatingToolbar render below).
  useEffect(() => {
    if (!editor) return;
    if (!editor.storage.textToRecord) return;
    editor.storage.textToRecord.onCreateTask = onCreateFromText
      ? () => {
          // No selection at the slash-trigger point (it's a caret, not a
          // range) — take the current block's text instead, same as typing
          // "/משימה" mid-paragraph turns that paragraph into a task.
          const { $from } = editor.state.selection;
          const text = $from.parent.textContent.trim();
          if (!text) return;
          void onCreateFromText('task', text);
        }
      : null;
  }, [editor, onCreateFromText]);

  // Sync external content changes (e.g., version restore) — skip internal edits
  useEffect(() => {
    if (!editor || !content) return;
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    // Never rebuild the document while a slash command is open.
    //
    // setContent() replaces the whole ProseMirror doc, which destroys EVERY
    // plugin's state — including the suggestion decoration that IS the slash
    // menu. The isInternalChange flag above is one-shot: the first render
    // after a keystroke consumes it, but autosave then produces extra renders
    // on its own timeline (saveState 'saving' → 'saved' → idle after 2000ms).
    // On one of those later renders the flag is already spent, and because
    // `content` state can lag the live doc, the JSON compare below sees a
    // difference and replaces the doc — closing the menu a couple of seconds
    // after it opened (and potentially reverting keystrokes with it).
    // Reported live twice as "the menu disappears on its own".
    const suggestion = SuggestionPluginKey.getState(editor.state) as { active?: boolean } | undefined;
    if (suggestion?.active) return;
    if (JSON.stringify(editor.getJSON()) !== JSON.stringify(content)) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  // ─── Slash menu ───────────────────────────────────
  // Visibility is owned HERE, not by the suggestion plugin. The extension
  // reports where the caret is and what was typed; the menu closes only on an
  // explicit dismissal (Escape / pick / click outside), so a spurious plugin
  // teardown can't take it down. `open: false` from the extension is treated
  // as "the trigger text is gone", which is a legitimate close — but it no
  // longer clears the caret while the user is still on the same slash.
  const [slash, setSlash] = useState<SlashState>(SLASH_CLOSED);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<GamSlashMenuEventDetail>).detail;

      // EVERY branch below must return the PREVIOUS state object when nothing
      // meaningful changed, so React bails out instead of re-rendering.
      // window.dispatchEvent runs listeners synchronously, and the extension
      // fires from inside a ProseMirror teardown — so a state object that is
      // merely new-by-reference re-renders the editor from within that
      // teardown, which fires the event again. That was an infinite loop
      // ("Maximum update depth exceeded"), not a slow render.
      // NOT closing on the extension's own exit — on purpose, and this is the
      // whole point of owning visibility here. The suggestion plugin tears
      // itself down for reasons we still haven't pinned down, and when the bar
      // honoured that it vanished the instant it appeared (reported: "/ opens
      // nothing"). The bar closes only on the three explicit dismissals below
      // (Escape / pick / click outside), which is what Gal asked for:
      // "לא רוצה שהתפריט יעלם עד שלא נעשה פעולה ברורה שמבטלת אותו".
      if (!detail?.open) return;

      // The raw caret box goes through untouched. The menu decides for
      // itself whether to hang below it or flip above, because only the menu
      // knows how tall it is — the old "always 44px above the caret" was a
      // number that fit a one-row strip and nothing else.
      const caret = detail.anchor ?? null;
      const range = detail.range ?? null;
      const categories = detail.categories ?? [];
      const query = detail.query ?? '';

      setSlash((prev) => {
        const sameCaret =
          prev.caret?.top === caret?.top && prev.caret?.left === caret?.left;
        const sameRange = prev.range?.from === range?.from && prev.range?.to === range?.to;
        // The catalogue is static and the extension filters it by `query`
        // alone, so an equal query means an equal list. Comparing the arrays
        // themselves would never match: they are rebuilt on every announce.
        const sameQuery = prev.query === query;
        if (sameCaret && sameRange && sameQuery) return prev;
        return { caret, range, categories, query };
      });
    };
    window.addEventListener(GAM_SLASH_MENU_EVENT, handler);
    return () => window.removeEventListener(GAM_SLASH_MENU_EVENT, handler);
  }, []);

  // Closes this editor's own bar and announces nothing. Kept apart from
  // dismissSlash so that reacting to someone else's dismissal can never
  // bounce an announcement back at them.
  const closeSlash = useCallback(() => {
    setSlash((prev) => (prev.caret === null ? prev : SLASH_CLOSED));
  }, []);

  // A dismissal the user actually made (Escape / pick / click outside).
  // Closes here AND tells every other surface mirroring this same slash —
  // today that is the Sidebar's תוספים panel — to close with it. Without
  // this announcement the panel has no legitimate close at all, because the
  // extension's own onExit is a lie it must ignore (see GAM_SLASH_DISMISS_EVENT).
  const dismissSlash = useCallback(() => {
    closeSlash();
    dispatchSlashDismiss();
  }, [closeSlash]);

  // The mirror surfaces dismiss too — picking a plugin from the sidebar
  // panel is every bit as explicit as picking one from the bar — so the bar
  // has to come down with them.
  useEffect(() => {
    window.addEventListener(GAM_SLASH_DISMISS_EVENT, closeSlash);
    return () => window.removeEventListener(GAM_SLASH_DISMISS_EVENT, closeSlash);
  }, [closeSlash]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        debouncedSave.cancel();
        if (onSave && editor) onSave(editor.getJSON());
      }
    },
    [editor, onSave, debouncedSave]
  );

  // Clicking the wrapper's own empty padding (below/around the last line —
  // the common case when content is short) previously did nothing: the
  // click never reached the ProseMirror node, so it never focused and
  // nothing could be typed. Forward it to end-of-document focus, matching
  // the standard "click anywhere in the page to keep writing" behavior.
  const handleWrapperMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && editor) {
        // Without this, the browser's own default mousedown behavior (blur —
        // the click target isn't a focusable element) runs after this handler
        // and undoes the focus() call below.
        e.preventDefault();
        editor.chain().focus('end').run();
      }
    },
    [editor]
  );

  if (!editor) return null;

  return (
    <div
      className="gam-editor-wrapper"
      dir={direction}
      onKeyDown={handleKeyDown}
      onMouseDown={handleWrapperMouseDown}
    >
      {editable && tier === 'full' && recordId && <div className="gam-placement-toolbar">
        <button type="button" aria-pressed={freeMode} onClick={() => { const next = !freeMode; setFreeMode(next); setFreePositionMode(editor, next); }}>מיקום חופשי</button>
        {freeMode && <span>לחץ במקום פנוי לכתיבה · הזז ושנה רוחב באמצעות ידיות הבלוק</span>}
      </div>}
      {editable && chrome.blockHandle && <BlockHandle editor={editor} />}
      {editable && chrome.floatingToolbar && (
        <FloatingToolbar
          editor={editor}
          gibberishConverter={gibberishConverter}
          aiTextAction={aiTextAction}
          onCreateFromText={onCreateFromText}
        />
      )}
      {editable && chrome.slashMenu && slash.caret && (
        <SlashFullMenu
          editor={editor}
          categories={slash.categories}
          query={slash.query}
          caret={slash.caret}
          range={slash.range}
          direction={direction}
          onDismiss={dismissSlash}
        />
      )}
      {searchEntities && tier === 'full' && (
        <ObjectRefPopup
          open={objectRefOpen}
          onClose={() => setObjectRefOpen(false)}
          searchEntities={searchEntities}
          onSelect={(ref) => editor?.commands.setObjectRef(ref)}
        />
      )}
      <EditorContent editor={editor} />
      {chrome.statusBar && (
        <StatusBar editor={editor} saveStatus={saveStatus} lastSavedAt={lastSavedAt} et={et} onConflictReload={onConflictReload} />
      )}
    </div>
  );
}

// ─── Status Bar ────────────────────────────────────────
function StatusBar({
  editor,
  saveStatus,
  lastSavedAt,
  et,
  onConflictReload,
}: {
  editor: ReturnType<typeof useEditor>;
  saveStatus: string;
  lastSavedAt?: Date;
  et: Record<string, string>;
  onConflictReload?: () => void;
}) {
  const text = editor?.getText() || '';
  const wordCount = useMemo(() => text.split(/\s+/).filter(Boolean).length, [text]);
  const charCount = text.length;
  const readingTime = Math.ceil(wordCount / 200);
  const relativeTime = useMemo(() => {
    if (!lastSavedAt) return '';
    const diff = Math.round((Date.now() - lastSavedAt.getTime()) / 1000);
    if (diff < 10) return et.timeJustNow;
    if (diff < 60) return et.timeSecondsAgo.replace('{{n}}', String(diff));
    const mins = Math.round(diff / 60);
    if (mins < 60) return et.timeMinutesAgo.replace('{{n}}', String(mins));
    const hrs = Math.round(mins / 60);
    return et.timeHoursAgo.replace('{{n}}', String(hrs));
  }, [lastSavedAt, et]);


  return (
    <div className="gam-editor-statusbar">
      <span className="gam-editor-statusbar__save">
        {saveStatus === 'saving' && <span className="text-blue-400">{et.saving}</span>}
        {saveStatus === 'saved' && <span className="text-emerald-400">{"✓ "}{et.saved}</span>}
        {saveStatus === 'retrying' && <span className="text-amber-400">{et.retrying}</span>}
        {saveStatus === 'error' && <span className="text-red-400">{"⚠ "}{et.saveError}</span>}
        {saveStatus === 'conflict' && (
          <span className="flex items-center gap-2 text-amber-400">
            <span>{"⚠ "}{et.conflictMessage}</span>
            <button
              onClick={() => onConflictReload ? onConflictReload() : window.location.reload()}
              className="rounded bg-amber-500/20 px-2 py-0.5 text-[11px] font-medium text-amber-300 hover:bg-amber-500/30 transition-colors"
            >
              {et.reload}
            </button>
          </span>
        )}
        {saveStatus === 'offline' && <span className="text-orange-400">{"☁ "}{et.offlineSavedLocally}</span>}
        {saveStatus === 'idle' && relativeTime && (
          <span className="text-slate-500">{et.lastSaved}: {relativeTime}</span>
        )}
      </span>
      <span className="gam-editor-statusbar__chars">
        {wordCount} {et.words} · {charCount} {et.chars}
        {readingTime > 0 && ` · ${readingTime} ${et.minRead}`}
      </span>
      <span className="gam-editor-statusbar__hint">
        <kbd>/</kbd> {et.menu} · <kbd>{typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl'}+S</kbd>
      </span>
    </div>
  );
}

export default TiptapEditor;
